import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Loader2,
  X as CloseIcon,
} from "lucide-react";

export const INPUT =
  "w-full bg-surface border border-border rounded-xl px-3.5 py-2.5 text-[15px] text-foreground placeholder:text-faint transition focus:border-foreground/40";

export function Spinner({ size = 18, className = "" }) {
  return <Loader2 size={size} className={`animate-spin ${className}`} />;
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="segmented-track text-sm">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 active:scale-[0.97] ${
              active
                ? "bg-background text-foreground shadow-sm"
                : "text-muted hover:text-foreground"
            }`}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`shrink-0 rounded-full p-[3px] transition-colors active:scale-[0.97] ${
        checked ? "bg-accent" : "bg-foreground/15"
      }`}
      style={{ width: 46, height: 28 }}
      role="switch"
      aria-checked={checked}
    >
      <span
        className="block rounded-full bg-white"
        style={{
          width: 22,
          height: 22,
          transform: checked ? "translateX(18px)" : "translateX(0)",
          transition: "transform 200ms cubic-bezier(0.32,0.72,0,1)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </button>
  );
}

export function IconBtn({ children, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="w-9 h-9 rounded-xl flex items-center justify-center text-muted transition hover:bg-foreground/5 hover:text-foreground active:scale-[0.97]"
    >
      {children}
    </button>
  );
}

export function Field({ label, hint, info, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {info ? <InfoTip text={info} /> : null}
        {hint ? <span className="text-xs text-faint ml-auto">{hint}</span> : null}
      </div>
      {children}
    </label>
  );
}

// A small info icon that reveals helper text on hover or tap.
export function InfoTip({ text, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <span ref={ref} className={`relative inline-flex align-middle ${className}`}>
      <button
        type="button"
        aria-label="More information"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="text-faint hover:text-foreground transition inline-flex items-center"
      >
        <Info size={14} />
      </button>
      {open ? (
        <span
          role="tooltip"
          className="absolute z-50 top-[calc(100%+8px)] left-0 w-64 max-w-[78vw] bg-popover border border-border rounded-xl p-3 text-[12.5px] leading-relaxed text-muted shadow-xl normal-case font-normal tracking-normal"
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

// Custom dropdown. options is an array of strings or { value, label }.
export function Select({ value, onChange, options, placeholder = "Select", className = "", buttonClassName = "" }) {
  const opts = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = opts.find((o) => o.value === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${INPUT} pr-9 text-left flex items-center ${buttonClassName}`}
      >
        <span className={`truncate ${selected ? "text-foreground" : "text-faint"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-faint transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="absolute z-50 mt-1.5 w-full max-h-64 overflow-auto bg-popover border border-border rounded-xl p-1 shadow-xl animate-fade-in">
          {opts.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-[14px] flex items-center gap-2 transition ${
                  active ? "bg-foreground/[0.06] text-foreground" : "text-muted hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <span className="truncate flex-1">{o.label}</span>
                {active ? <Check size={15} className="shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const DP_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DP_DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const dpParse = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dpISO = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Custom date picker. value and onChange use a "yyyy-mm-dd" string (or "").
export function DatePicker({ value, onChange, placeholder = "Select date", className = "", allowClear = true }) {
  const [open, setOpen] = useState(false);
  const selected = dpParse(value);
  const [view, setView] = useState(selected || new Date());
  const ref = useRef(null);

  useEffect(() => {
    const d = dpParse(value);
    if (d) setView(d);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const y = view.getFullYear();
  const m = view.getMonth();
  const startOffset = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(y, m, d));
  const todayISO = dpISO(new Date());
  const label = selected
    ? selected.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${INPUT} pr-9 text-left flex items-center`}
      >
        <span className={`truncate ${selected ? "text-foreground" : "text-faint"}`}>
          {selected ? label : placeholder}
        </span>
        <CalendarIcon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-faint" />
      </button>
      {open ? (
        <div className="absolute z-50 mt-1.5 w-[18rem] max-w-[90vw] bg-popover border border-border rounded-2xl p-3 shadow-xl animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setView(new Date(y, m - 1, 1))}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-[13.5px] font-medium text-foreground">
              {DP_MONTHS[m]} {y}
            </div>
            <button
              type="button"
              onClick={() => setView(new Date(y, m + 1, 1))}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DP_DOW.map((d) => (
              <div key={d} className="text-center text-[11px] text-faint py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((dt, i) => {
              if (!dt) return <div key={`e${i}`} />;
              const iso = dpISO(dt);
              const isSel = value === iso;
              const isToday = iso === todayISO;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                  className={`h-9 rounded-lg text-[13px] flex items-center justify-center transition ${
                    isSel
                      ? "bg-accent text-accent-foreground font-medium"
                      : isToday
                        ? "text-foreground ring-1 ring-border hover:bg-foreground/5"
                        : "text-muted hover:bg-foreground/5 hover:text-foreground"
                  }`}
                >
                  {dt.getDate()}
                </button>
              );
            })}
          </div>
          {allowClear ? (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  onChange(dpISO(new Date()));
                  setOpen(false);
                }}
                className="text-[12.5px] text-muted hover:text-foreground px-2 py-1 rounded-lg transition"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="text-[12.5px] text-muted hover:text-foreground px-2 py-1 rounded-lg transition"
              >
                Clear
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function PrimaryBtn({ children, onClick, disabled, full }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`bg-accent text-accent-foreground rounded-xl px-4 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 whitespace-nowrap transition hover:opacity-90 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none ${
        full ? "w-full" : ""
      }`}
    >
      {children}
    </button>
  );
}

export function GhostBtn({ children, onClick, full, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={`border border-border rounded-xl px-4 py-2.5 text-sm font-medium text-foreground flex items-center justify-center gap-2 whitespace-nowrap transition hover:bg-foreground/5 active:scale-[0.97] ${
        full ? "w-full" : ""
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function Sheet({ open, onClose, title, children, footer, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6">
      <div
        className="absolute inset-0 animate-backdrop-in"
        style={{ background: "rgba(0,0,0,0.5)" }}
        onClick={onClose}
      />
      <div
        className={`relative w-full ${
          wide ? "sm:max-w-3xl" : "sm:max-w-lg"
        } bg-popover border-t sm:border border-border rounded-t-3xl sm:rounded-3xl animate-sheet-up sm:animate-slide-up max-h-[90dvh] sm:max-h-[85vh] flex flex-col overflow-hidden shadow-2xl`}
      >
        <div className="sm:hidden shrink-0 flex justify-center pt-2.5 pb-1">
          <span className="h-1.5 w-10 rounded-full bg-foreground/20" />
        </div>
        <div className="shrink-0 flex items-center justify-between px-5 py-3 sm:py-3.5 border-b border-border bg-popover">
          <h3 className="text-[16px] sm:text-[17px] font-medium text-foreground">{title}</h3>
          <IconBtn onClick={onClose} title="Close">
            <CloseIcon size={20} />
          </IconBtn>
        </div>
        <div className="overflow-y-auto overscroll-contain px-5 py-5 flex-1">{children}</div>
        {footer ? (
          <div className="shrink-0 px-5 py-3.5 border-t border-border bg-popover flex items-center justify-end gap-2.5 safe-bottom">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function EmptyHint({ icon, title, body, action }) {
  return (
    <div className="bg-surface border border-border rounded-2xl py-14 px-6 text-center flex flex-col items-center animate-fade-in">
      <div className="w-14 h-14 rounded-2xl bg-foreground/5 flex items-center justify-center text-muted mb-4">
        {icon}
      </div>
      <h3 className="text-[17px] font-semibold text-foreground">{title}</h3>
      <p className="text-[14px] text-muted mt-1.5 max-w-md">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

// Neutral toast. No semantic color: a small foreground dot plus the message.
export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[70] animate-slide-up">
      <div className="bg-popover border border-border rounded-full px-4 py-2.5 flex items-center gap-2.5 text-[13.5px] font-medium text-foreground shadow-xl">
        <span className="w-1.5 h-1.5 rounded-full bg-foreground" />
        {toast.msg}
      </div>
    </div>
  );
}

export function GeneratingOverlay({ gen, logoSlot }) {
  if (!gen.active) return null;
  const pct = gen.total ? Math.round((gen.done / gen.total) * 100) : 0;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 glass animate-backdrop-in" />
      <div className="relative bg-popover border border-border rounded-3xl p-7 w-[340px] text-center animate-slide-up shadow-2xl">
        <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4 text-accent-foreground">
          {logoSlot || <Spinner size={26} />}
        </div>
        <h3 className="text-[17px] font-semibold text-foreground">Generating your calendar</h3>
        <p className="text-[13.5px] text-muted mt-1.5 min-h-[20px]">{gen.status}</p>
        <div className="mt-5 h-2 rounded-full overflow-hidden bg-foreground/5">
          <div
            className="h-full bg-accent"
            style={{ width: `${pct}%`, transition: "width 400ms cubic-bezier(0.32,0.72,0,1)" }}
          />
        </div>
        <div className="text-[12px] text-faint mt-2">
          {gen.done} / {gen.total} platforms
        </div>
      </div>
    </div>
  );
}

export function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="fixed bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-[55] w-[92%] max-w-md bg-popover border border-border rounded-2xl p-4 animate-slide-up shadow-xl">
      <div className="flex gap-3">
        <AlertCircle size={20} className="shrink-0 mt-0.5 text-foreground" />
        <div className="flex-1">
          <div className="text-[14px] font-semibold text-foreground">Generation unavailable</div>
          <p className="text-[13px] text-muted mt-1">{message}</p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={onDismiss}
              className="text-[13px] font-medium px-3 py-1.5 rounded-lg text-muted hover:bg-foreground/5 transition active:scale-[0.97]"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Check };

// Small download dropdown. options: [{ key, label }]; onPick(key) fires on select.
export function ExportMenu({ options, onPick, label = "Export", full }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <GhostBtn full={full} onClick={() => setOpen((o) => !o)}>
        <Download size={15} />
        {label}
      </GhostBtn>
      {open ? (
        <div className="absolute right-0 mt-1.5 w-44 bg-popover border border-border rounded-xl shadow-xl z-30 overflow-hidden py-1">
          {options.map((o) => (
            <button
              key={o.key}
              onClick={() => {
                setOpen(false);
                onPick(o.key);
              }}
              className="w-full text-left px-3.5 py-2 text-sm text-foreground hover:bg-foreground/5 transition"
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}